// src/components/MainNav.tsx
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { forwardRef } from "react";
import { navLinks } from "./NavbarComponents/navData";

const MainNav = ({ className }: { className?: string }) => {
  const location = useLocation();
  
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <NavigationMenu className={cn("flex justify-center", className)}>
      <NavigationMenuList className="flex space-x-1">
        {navLinks.map((link) => (
          <NavigationMenuItem key={link.name}>
            {link.hasDropdown ? (
              <>
                <NavigationMenuTrigger
                  className={cn(
                    "text-white font-semibold hover:text-secondary hover:bg-transparent",
                    isActive(link.path) && "bg-white text-secondary font-bold"
                  )}
                >
                  {link.name}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid gap-3 p-4 w-[240px] md:w-[340px] md:grid-cols-1 bg-white shadow-xl rounded-md border border-gray-200">
                    <li className="pb-2 border-b border-gray-100">
                      <Link to={link.overview?.path || link.path} className="block p-2 rounded hover:bg-gray-50">
                        <div className="font-medium">{link.overview?.name || `Pregled ${link.name.toLowerCase()}`}</div>
                        <p className="text-sm text-muted-foreground">Pregled vseh {link.name.toLowerCase()}</p>
                      </Link>
                    </li>
                    {link.dropdownItems?.map((item) => (
                      <ListItem
                        key={item.name}
                        title={item.name}
                        href={item.path}
                        className={isActive(item.path) ? "bg-[#F2FCE2]" : ""}
                      />
                    ))}
                  </ul>
                </NavigationMenuContent>
              </>
            ) : (
              <NavigationMenuLink asChild>
                <Link 
                  to={link.path}
                  className={cn(
                    navigationMenuTriggerStyle(),
                    "text-white font-semibold hover:text-secondary hover:bg-transparent",
                    isActive(link.path) && "bg-white text-secondary font-bold"
                  )}
                >
                  {link.name}
                </Link>
              </NavigationMenuLink>
            )}
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

const ListItem = forwardRef<
  React.ElementRef<"a">,
  React.ComponentPropsWithoutRef<"a"> & { className?: string }
>(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground text-stone-800 bg-white",
            className
          )}
          {...props}
        >
          <div className="text-sm font-medium leading-none text-stone-800">{title}</div>
          <p className="line-clamp-2 text-sm leading-snug text-stone-600">
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";

export default MainNav;